import React, { useState, useEffect } from "react";
import { useRefinementList } from "react-instantsearch";
import { CgSearch } from "react-icons/cg";
import { IoClose } from "react-icons/io5";

// Custom hook to get categories with the InstantSearch refinement list
const useCategoryRefinementList = (searchClient) => {
  const { items, refine } = useRefinementList({
    attribute: "category",
    limit: 1000,
    sortBy: ["name:asc"],
  });

  return { items, refine };
};

const CategoryFilterModal = ({ isOpen, onClose, searchClient }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const { items, refine } = useCategoryRefinementList(searchClient);

  // Get currently selected categories from instantsearch
  useEffect(() => {
    if (items) {
      const selected = items
        .filter((item) => item.isRefined)
        .map((item) => item.value);
      setSelectedCategories(selected);
    }
  }, [items]);

  // Handle category selection
  const handleCategorySelect = (value) => {
    refine(value);
  };

  // Apply filters and close modal
  const applyFilters = () => {
    onClose();
  };

  // Clear all filters
  const clearFilters = () => {
    selectedCategories.forEach((category) => {
      refine(category);
    });
    onClose();
  };

  // Filter categories based on search query
  const filteredCategories = items
    ? items.filter((item) =>
        item.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  if (!isOpen) return null;

  return (
    <>
      {/* Full page backdrop overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[200]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal content - positioned to completely cover the search word area */}
      <div
        className="fixed inset-0 md:inset-y-0 md:right-0 md:left-auto md:w-[350px] md:max-w-[90vw] 
                  bg-white dark:bg-pl_bg_dark_3 rounded-none shadow-xl z-[201] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
      >
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b dark:border-pl_border_dark_1 border-pl_border_light_1">
          <h2
            id="modal-headline"
            className="text-lg font-semibold dark:text-pl_txt_dark_3 text-pl_txt_light_4"
          >
            Filter by Category
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <IoClose className="w-6 h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Search input */}
        <div className="px-4 py-3 border-b dark:border-pl_border_dark_1 border-pl_border_light_1">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search categories..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border dark:border-pl_border_dark_1 border-pl_border_light_1
                       dark:bg-pl_bg_dark_4 bg-pl_bg_light_1 dark:text-pl_txt_dark_3 text-pl_txt_light_3
                       focus:outline-none focus:ring-2 focus:ring-pl_prim_color_1"
            />
            <CgSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
          </div>
        </div>

        {/* Flat list of categories with pink checkboxes */}
        <div className="flex-grow overflow-y-auto px-4 py-2">
          {filteredCategories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {filteredCategories.map((category) => (
                <div key={category.value} className="mb-1">
                  <label className="flex items-center p-2 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                    {/* Custom styled checkbox with pink color */}
                    <div className="relative flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={category.isRefined}
                        onChange={() => handleCategorySelect(category.value)}
                        className="appearance-none h-4 w-4 border rounded border-gray-300 dark:border-gray-600 
                                checked:border-pl_prim_color_1 focus:outline-none"
                      />
                      {category.isRefined && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="h-4 w-4 bg-pl_prim_color_1 rounded">
                            <svg
                              className="h-4 w-4 text-white"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                    <span className="ml-2 text-sm dark:text-pl_txt_dark_3 text-pl_txt_light_3">
                      {category.label}
                    </span>
                    <span className="ml-auto text-xs bg-gray-200 dark:bg-gray-700 rounded-full px-2 py-0.5 dark:text-pl_txt_dark_2 text-pl_txt_light_2">
                      {category.count}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex justify-center items-center h-40 text-gray-500 dark:text-gray-400">
              No categories found matching your search.
            </div>
          )}
        </div>

        {/* Footer with action buttons */}
        <div className="px-4 py-3 border-t dark:border-pl_border_dark_1 border-pl_border_light_1 flex justify-end space-x-3">
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm font-medium rounded-lg border dark:border-pl_border_dark_1 border-pl_border_light_1
                     dark:text-pl_txt_dark_3 text-pl_txt_light_3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Clear
          </button>
          <button
            onClick={applyFilters}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-pl_prim_color_1 text-white
                     hover:bg-pl_prim_color_2 transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
};

export default CategoryFilterModal;
